import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowUpRightIcon } from 'lucide-react';
import { SubpageLayout } from '../components/SubpageLayout';
import { journalPosts } from '../data/journal';

/** Journal article page. */
export function Article() {
  const {
    slug
  } = useParams();
  const index = journalPosts.findIndex(p => p.slug === slug);
  if (index === -1) return <Navigate to="/" replace data-id="element-427" data-mp="xx4nzvn17yvg" />;
  const post = journalPosts[index];
  const more = [1, 2].map(n => journalPosts[(index + n) % journalPosts.length]);
  return <SubpageLayout title={post.title} data-id="element-428" data-mp="txnjp5jcd7rv">
      <article className="px-page pb-20 pt-36 md:pb-28 md:pt-44" data-id="element-429" data-mp="p3rpme67851z">
        <Link to="/#journal" className="group inline-flex items-center gap-2 text-[13px] text-fg/50 transition-colors duration-200 hover:text-fg" data-id="element-430" data-mp="k5hprq9j4ga1">
          <ArrowLeftIcon className="h-3.5 w-3.5 transition-transform duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:-translate-x-0.5" aria-hidden="true" data-id="element-431" data-mp="1hmymsto5ze96" />
          All news
        </Link>

        <div className="mt-10 flex items-center gap-2 text-[13px]" data-id="element-432" data-mp="1lsu6z56s1ngl">
          <span className="text-fg" data-id="element-433" data-mp="1lu5ad3wb8t76">{post.category}</span>
          <span className="h-3 w-px bg-line" aria-hidden="true" data-id="element-434" data-mp="1804j0v7ou8gu" />
          <time className="text-fg/45" data-id="element-435" data-mp="1c297lfja4ewm">{post.date}</time>
          <span className="h-3 w-px bg-line" aria-hidden="true" data-id="element-436" data-mp="1c0oi44btnm5m" />
          <span className="text-fg/45" data-id="element-437" data-mp="y1oqqowxct5r">{post.readTime}</span>
        </div>

        <h1 className="mt-4 max-w-[900px] font-serif text-[36px] font-normal leading-[1.08] tracking-[-0.01em] md:text-[56px]" data-id="element-438" data-mp="131jfalt896nn">
          {post.title}
        </h1>
        <p className="mt-6 max-w-[640px] text-[18px] leading-[1.5] text-fg/60" data-id="element-439" data-mp="p44pvh2z4yvo">{post.excerpt}</p>

        <div className="mt-12 aspect-[16/9] overflow-hidden bg-fg/5 md:mt-16" data-id="element-440" data-mp="t5pwwlstuixy">
          <img src={post.image} alt="" className="h-full w-full object-cover" data-id="element-441" data-mp="2jgkphz19lerk" />
        </div>

        <div className="mt-12 grid md:mt-16 md:grid-cols-12 md:gap-x-6" data-id="element-442" data-mp="2ngd0eie4rx03">
          <div className="space-y-5 md:col-span-7 md:col-start-4" data-id="element-443" data-mp="9gzcu5qb31q">
            {post.body.map((block, i) => block.startsWith('## ') ? <h2 key={i} className="pt-6 text-[22px] leading-[1.3] tracking-[-0.01em] text-fg" data-id="element-444" data-mp="3rhuvhoqr0n4n">
                  {block.slice(3)}
                </h2> : <p key={i} className="text-[17px] leading-[1.65] text-fg/70" data-id="element-445" data-mp="3igekpfw0uizw">
                  {block}
                </p>)}
          </div>
        </div>
      </article>

      {/* More from the journal */}
      <section aria-labelledby="more-heading" className="border-t border-line px-page pb-20 pt-12 md:pb-28" data-id="element-446" data-mp="f0hax81rvd23">
        <h2 id="more-heading" className="text-[12px] font-medium uppercase tracking-[0.04em] text-fg/60" data-id="element-447" data-mp="58bkw3v2z5pj">
          More from the journal
        </h2>
        <ul className="mt-6 grid gap-x-[var(--gutter)] gap-y-10 md:grid-cols-2" data-id="element-448" data-mp="12p8td7pk8h1o">
          {more.map(p => <li key={p.slug} data-id="element-449" data-mp="xvo5w9llf5gq">
              <Link to={p.href} className="group flex flex-col focus:outline-none" data-id="element-450" data-mp="1583w9nc45dut">
                <div className="aspect-[16/10] overflow-hidden bg-fg/5 group-focus-visible:ring-2 group-focus-visible:ring-fg" data-id="element-451" data-mp="15agk2h4jyifs">
                  <img src={p.image} alt="" loading="lazy" className="h-full w-full object-cover opacity-[0.88] transition-opacity duration-200 group-hover:opacity-100" data-id="element-452" data-mp="1f5s2paj309st" />
                </div>
                <div className="mt-4 flex items-start justify-between gap-6" data-id="element-453" data-mp="1t5t3kpzcjh8a">
                  <div data-id="element-454" data-mp="1t3q3fd4hc7pu">
                    <p className="text-[13px] text-fg/45" data-id="element-455" data-mp="1xw9f24jlwss0">
                      {p.category} · {p.date}
                    </p>
                    <h3 className="mt-1 text-[19px] leading-[1.3] tracking-[-0.01em] text-fg/85 transition-colors duration-200 group-hover:text-fg md:text-[20px]" data-id="element-456" data-mp="22ukowf9wj3a8">
                      {p.title}
                    </h3>
                  </div>
                  <ArrowUpRightIcon className="mt-1 h-4 w-4 shrink-0 text-fg/50 transition-[transform,color] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-fg" aria-hidden="true" data-id="element-457" data-mp="2bu7qp26jmb3t" />
                </div>
              </Link>
            </li>)}
        </ul>
      </section>
    </SubpageLayout>;
}